/**
 * Glue a TableDriver to the store and the CueQueue. Authoritative state → `table` /
 * `privateView`; cues → queue; connection → `session.connection`; a snapshot resync flushes
 * the queue so the scene re-hydrates from `table` (DESIGN-client §1.2, DESIGN-server §4).
 */
import type { SpadesStore } from '../store/store';
import { SOCKET_ERROR_CODE, type SeatView, type TableView } from './types-local';
import type { CueQueue } from './CueQueue';
import type { TableDriver } from './TableDriver';

/**
 * How the scene rebuilds itself from authoritative state after a reconnect.
 *
 * The server answers a reconnect with `cues {snapshot:true}`, which means "the gap is too old (or
 * you are new), render from the schema". Flushing the queue on its own leaves the table frozen
 * mid-animation with nothing to rebuild it, so the scene registers here and `wireDriver` calls it
 * right after the flush. Registered by the scene (`SceneRoot.hydrate`) so `net/` never imports it.
 */
type SnapshotHydrator = (table: TableView | null, priv: SeatView | null) => void;

let hydrator: SnapshotHydrator | null = null;

/** The live driver's clock, for `serverNow()` below. Set by `wireDriver`, cleared on unwire. */
let clock: (() => number) | null = null;

/**
 * The driver that currently owns the table, and therefore the one a player's intents belong to.
 *
 * "Currently" matters, because there is more than one. Boot wires the real driver for the session;
 * the tutorial then wires a `ScriptedDriver` of its own over the top for as long as it is on screen
 * (`tutorial/Tutorial.tsx`), and unwires back to the first on the way out. The scene has one `send`
 * port and cannot know which is which, so `wireDriver` records itself here and restores its
 * predecessor on unwire — a stack, in the same shape as `clock` above.
 *
 * Before this, `TableScreen` sent every intent to `getRuntime().driver` — the boot driver, always.
 * So the tutorial's own script never heard the player at all: the bid it was waiting for went to a
 * Colyseus room that was not connected, and the first hand could only be advanced with the Next
 * button. That is the other half of why the tutorial autoran.
 */
let sink: TableDriver | null = null;

/** Send an intent to whichever driver is wired to the store right now. */
export function sendIntent(intent: string, payload: unknown): void {
  sink?.send(intent as never, payload as never);
}

/** The wired driver (test seam). */
export function intentSink(): TableDriver | null {
  return sink;
}

/**
 * The server's clock. Everything that draws a countdown — the HUD bar, the seat rings — must use
 * this, because `TimerSchema.startedAt`/`endsAt` are server timestamps and a device clock that is
 * even a minute off would render every timer as already expired. Falls back to `Date.now()` when
 * there is no driver, which is right for the scripted driver and harmless before boot.
 */
export function serverNow(): number {
  return clock ? clock() : Date.now();
}

/**
 * True for a socket-level error rather than a server verdict on an intent.
 *
 * The room's own `error` codes are always names (`not_your_turn`, `illegal_card`, `bad_phase`);
 * the SDK's socket errors carry the websocket close code, or `socket` when there is none. Only the
 * first kind describes something the scene can repair.
 */
export function isTransportErrorCode(code: string): boolean {
  return code === SOCKET_ERROR_CODE || /^\d+$/.test(code);
}

export function setSnapshotHydrator(fn: SnapshotHydrator | null): () => void {
  hydrator = fn;
  return () => {
    if (hydrator === fn) hydrator = null;
  };
}

export function wireDriver(driver: TableDriver, store: SpadesStore, cueQueue: CueQueue): () => void {
  const s = store.getState();
  const offs = [
    driver.onState((view) => {
      const st = store.getState();
      st.setTable(view);
      // Keep `session.seat` in sync with where our player actually sits.
      const me = st.session.me;
      if (me) {
        const idx = view.seats.findIndex((seat) => seat.playerId === me.id);
        const seat = idx >= 0 ? (idx as 0 | 1 | 2 | 3) : null;
        if (seat !== st.session.seat) st.setSession({ seat });
      }
    }),
    driver.onPrivate((v) => {
      const st = store.getState();
      st.setPrivateView(v);
      if (st.session.seat !== v.me) st.setSession({ seat: v.me });
    }),
    driver.onCue((cue) => cueQueue.push(cue)),
    driver.onConnection((c) => {
      const reason = c === 'lost' ? (driver as { lostReason?: string | null }).lostReason ?? null : null;
      store.getState().setSession({ connection: c, connectionReason: reason });
    }),
    driver.onResync?.((kind) => {
      if (kind !== 'snapshot') return;
      cueQueue.flush();
      // Flush alone would leave the scene showing whatever the aborted handler left behind.
      const st = store.getState();
      try {
        hydrator?.(st.table, st.privateView);
      } catch (err) {
        console.warn('[table] scene hydrate after resync failed', err);
      }
    }) ?? (() => {}),
    // A refusal has to reach the surface that has to undo something, not only the console.
    //
    // The scene is optimistic: the bid stack goes terminal the moment a bid is committed, the card
    // leaves the hand the moment it is played. Warning and dropping the refusal meant a player
    // whose bid the server rejected was stuck on a locked stack for the rest of the hand (client
    // review #2). Transport errors are excluded — they belong to `connection`, and publishing one
    // here would make the scene try to repair a surface nothing is wrong with.
    driver.onError?.((code, msg, intent) => {
      console.warn(`[table] ${code}${intent ? ` (${intent})` : ''}: ${msg}`);
      if (isTransportErrorCode(code)) return;
      store.getState().setSession({ lastIntentError: { code, msg, intent: intent ?? null, at: Date.now() } });
    }) ?? (() => {}),
    // The end-of-match XP snapshot, straight into the store for the end screen to animate. It
    // arrives once and is never replayed, so the store is the only place it survives the trip from
    // the socket to a React render; `clearProgress` on the next match start is what retires it.
    driver.onProgress?.((msg) => store.getState().setLastProgress(msg)) ?? (() => {}),
  ];
  clock = driver.serverNow ? () => driver.serverNow!() : null;
  const previousSink = sink;
  sink = driver;

  // A reload mid-match keeps the reconnection token in sessionStorage, so put the player back in
  // their seat before they touch anything. Nothing is created when there is no token, so this
  // settles back to 'idle' and the lobby offers Quick Play as usual (DESIGN-server §3.5).
  // 'idle' matters: the Lobby's Discord auto-join and `?code=` deep link both wait for it.
  const idle = () => store.getState().setSession({ connection: 'idle' });
  if (driver.resume) {
    s.setSession({ connection: 'connecting' });
    void driver.resume().then((resumed) => {
      if (!resumed) idle();
    }, idle);
  } else {
    idle();
  }
  return () => {
    clock = null;
    // Back to whoever was wired before, not to nothing: the tutorial unwires on its way to the
    // lobby and the session's own driver has to keep taking intents afterwards.
    if (sink === driver) sink = previousSink;
    offs.forEach((off) => off());
  };
}
